import { inject } from '@angular/core';
import * as AlertActions from '@curbnturf/alert/src/lib/+state/alert.actions';
import {
  Boondock,
  BoondockPoint,
  IUserCustomLocation,
  LatLon,
  Listing,
  ListingPoint,
  ListingType,
  LogLevel,
  MapPoint,
  NGRX_NO_ACTION,
  SearchQuery,
  UserCustomLocation,
} from '@curbnturf/entities';
import { stringFromError } from '@curbnturf/form-helpers';
import { ListingService } from '@curbnturf/listing-search/src/lib/listing.service';
import * as LogActions from '@curbnturf/network/src/lib/log/+state/log.actions';
import { Logger } from '@curbnturf/network/src/lib/log/logger';
import * as NetworkSelectors from '@curbnturf/network/src/lib/network/+state/network.selectors';
import { UserCustomLocationService } from '@curbnturf/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, throttleTime, withLatestFrom } from 'rxjs/operators';
import { BoondockDisplayService } from '../boondock-display.service';
import { BoondockService } from '../boondock.service';
import { SearchService } from '../search.service';
import * as ListingSearchActions from './listing-search.actions';
import { ListingSearchActionTypes } from './listing-search.actions';
import * as ListingSearchSelectors from './listing-search.selectors';

export const runListingSearch$ = createEffect(
  (actions$ = inject(Actions), listingSearchService = inject(SearchService), logger = inject(Logger)) =>
    actions$.pipe(
      ofType(ListingSearchActions.runListingSearch),
      throttleTime(1000),
      tap((payload) => logger.debug('Running Listing Search', JSON.stringify(payload))),
      switchMap((action) => {
        return listingSearchService.find(new SearchQuery(action.query)).pipe(
          map((listingSearchResults: { listings: ListingPoint[]; boondockListings: BoondockPoint[] }) => {
            const results = listingSearchResults?.listings?.map(
              (el) =>
                new ListingPoint({
                  ...el,
                }),
            );
            const boondockResults = listingSearchResults?.boondockListings?.map(
              (el) =>
                new BoondockPoint({
                  ...el,
                }),
            );
            return ListingSearchActions.listingSearchCompleted({ results, boondockResults });
          }),
          catchError((err) => {
            logger.error(ListingSearchActionTypes.ListingSearchError, JSON.stringify(err));
            return of(ListingSearchActions.listingSearchError({ error: err }));
          }),
        );
      }),
    ),
  { functional: true },
);

export const runListingTypeahead$ = createEffect(
  (actions$ = inject(Actions), listingSearchService = inject(SearchService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.runListingTypeahead),
      throttleTime(1000),
      switchMap((action) =>
        listingSearchService.find(new SearchQuery(action.query)).pipe(
          map((listingSearchResults: { listings: ListingPoint[]; boondockListings: BoondockPoint[] }) => {
            const results = listingSearchResults?.listings?.map(
              (el) =>
                new ListingPoint({
                  ...el,
                }),
            );
            const boondockResults = listingSearchResults?.boondockListings?.map(
              (el) =>
                new BoondockPoint({
                  ...el,
                }),
            );
            return ListingSearchActions.listingTypeaheadCompleted({ results, boondockResults });
          }),
          catchError(() => of(ListingSearchActions.listingTypeaheadCleared())),
        ),
      ),
    ),
  { functional: true },
);

export const listingSearchError$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingSearchActions.listingSearchError),
      map((action) =>
        AlertActions.displayAlert({
          title: 'Error Searching for Properties',
          body: stringFromError(action.error),
          level: 'error',
        }),
      ),
    ),
  { functional: true },
);

export const selectPoint$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.selectPoint),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedListing)),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedPoint)),
      switchMap(([[action, selectedListing], selectedPoint]) => {
        if (!action.point) {
          return [
            ListingSearchActions.listingDisplaySelected({ listing: undefined }),
            ListingSearchActions.boondockDisplaySelected({ boondock: undefined }),
          ];
        }

        if (selectedPoint && action.point.id === selectedPoint?.id) {
          const actions = [];
          if (selectedListing && selectedListing.id === selectedPoint.id) {
            actions.push(ListingSearchActions.listingDisplaySelected({ listing: selectedListing }));
          } else {
            return listingService.fetch(action.point.id).pipe(
              mergeMap((result) => {
                const listing: Listing = new Listing({ ...result });
                return [ListingSearchActions.listingDisplaySelected({ listing })];
              }),
              catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
            );
          }

          return [...actions, NGRX_NO_ACTION];
        }

        if (action.point.id) {
          return listingService.fetch(action.point.id).pipe(
            mergeMap((result) => {
              const listing: Listing = new Listing({ ...result });
              return [ListingSearchActions.listingDisplaySelected({ listing })];
            }),
            catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
          );
        }

        return [NGRX_NO_ACTION];
      }),
    ),
  { functional: true },
);

export const selectBoondockPointById$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), boondockService = inject(BoondockDisplayService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.selectBoondockPointById),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedBoondock)),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedBoondockPoint)),
      withLatestFrom(store.select(ListingSearchSelectors.getBoondockPoints)),
      switchMap(([[[action, selectedBoondock], selectedPoint], allPoints]) => {
        if (!action.pointId) {
          return [
            ListingSearchActions.listingDisplaySelected({ listing: undefined }),
            ListingSearchActions.boondockDisplaySelected({ boondock: undefined }),
          ];
        }

        const point = allPoints.find((el) => el?.id === action.pointId);
        if (point) {
          if (selectedPoint && action.pointId === selectedPoint?.id) {
            const actions = [];
            if (selectedBoondock && selectedBoondock.id === selectedPoint.id) {
              actions.push(ListingSearchActions.boondockDisplaySelected({ boondock: selectedBoondock }));
            } else {
              return boondockService.fetch(action.pointId).pipe(
                mergeMap((result) => {
                  const boondock: Boondock = new Boondock({ ...result });
                  return [ListingSearchActions.boondockDisplaySelected({ boondock })];
                }),
                catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
              );
            }

            return [...actions, NGRX_NO_ACTION];
          }

          return boondockService.fetch(action.pointId).pipe(
            mergeMap((result) => {
              const boondock: Boondock = new Boondock({ ...result });
              return [ListingSearchActions.boondockDisplaySelected({ boondock })];
            }),
            catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
          );
        }

        // Couldn't find the map point, so resetting everything to undefined.
        return [
          ListingSearchActions.listingDisplaySelected({ listing: undefined }),
          ListingSearchActions.boondockDisplaySelected({ boondock: undefined }),
        ];
      }),
    ),
  { functional: true },
);

export const selectPointById$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService), boondockService = inject(BoondockService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.selectPointById),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedListing)),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedBoondock)),
      withLatestFrom(store.select(ListingSearchSelectors.getAllPoints)),
      switchMap(([[[action, selectedListing], selectedBoondock], allPoints]) => {
        if (!action.pointId) {
          return [
            ListingSearchActions.listingDisplaySelected({ listing: undefined }),
            ListingSearchActions.boondockDisplaySelected({ boondock: undefined }),
          ];
        }

        if (!action.pointType || action.pointType === ListingType.standard) {
          const point = allPoints.listings.find((el) => el?.id === action.pointId);
          if (point) {
            if (selectedListing && action.pointId === selectedListing?.id) {
              const actions: Action[] = [];
              if (selectedListing && selectedListing.id === selectedListing.id) {
                actions.push(ListingSearchActions.listingDisplaySelected({ listing: selectedListing }));
              } else {
                return listingService.fetch(action.pointId).pipe(
                  mergeMap((result) => {
                    const listing: Listing = new Listing({ ...result });
                    return [ListingSearchActions.listingDisplaySelected({ listing })];
                  }),
                  catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
                );
              }
              return [...actions, NGRX_NO_ACTION];
            }
            return listingService.fetch(action.pointId).pipe(
              mergeMap((result) => {
                const listing: Listing = new Listing({ ...result });
                return [ListingSearchActions.listingDisplaySelected({ listing })];
              }),
              catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
            );
          }
        } else {
          const point = allPoints.boondocks.find((el) => el?.id === action.pointId);
          if (point) {
            if (selectedBoondock && action.pointId === selectedBoondock?.id) {
              const actions: Action[] = [];
              if (selectedBoondock && selectedBoondock.id === selectedBoondock.id) {
                actions.push(ListingSearchActions.boondockDisplaySelected({ boondock: selectedBoondock }));
              } else {
                return boondockService.fetch(action.pointId).pipe(
                  mergeMap((result) => {
                    const boondock: Boondock = new Boondock({ ...result });
                    return [ListingSearchActions.boondockDisplaySelected({ boondock })];
                  }),
                  catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
                );
              }
              return [...actions, NGRX_NO_ACTION];
            }
            return boondockService.fetch(action.pointId).pipe(
              mergeMap((result) => {
                const boondock: Boondock = new Boondock({ ...result });
                return [ListingSearchActions.boondockDisplaySelected({ boondock })];
              }),
              catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
            );
          }
        }

        // Couldn't find the map point, so resetting everything to undefined.
        return [
          ListingSearchActions.listingDisplaySelected({ listing: undefined }),
          ListingSearchActions.boondockDisplaySelected({ boondock: undefined }),
        ];
      }),
    ),
  { functional: true },
);

export const selectListingById$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), listingService = inject(ListingService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.selectListingById),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedListing)),
      withLatestFrom(store.select(ListingSearchSelectors.getSelectedPoint)),
      withLatestFrom(store.select(ListingSearchSelectors.getListingPoints)),
      switchMap(([[[action, selectedListing], selectedPoint], listingPoints]) => {
        if (!action.listingId) {
          return [
            ListingSearchActions.listingDisplaySelected({ listing: undefined }),
          ];
        }

        const point = listingPoints.find((el) => el?.id === action.listingId);
        if (point) {
          if (selectedPoint && action.listingId === selectedPoint?.id) {
            const actions: Action[] = [];
            if (selectedListing && selectedListing.id === selectedPoint.id) {
              actions.push(ListingSearchActions.listingDisplaySelected({ listing: selectedListing }));
            } else {
              return listingService.fetch(action.listingId).pipe(
                mergeMap((result) => {
                  const listing: Listing = new Listing({ ...result });
                  return [ListingSearchActions.listingDisplaySelected({ listing })];
                }),
                catchError((err) => {
                  return of(ListingSearchActions.listingSearchError({ error: err }));
                }),
              );
            }
            return [...actions, NGRX_NO_ACTION];
          }
        } else {
          return listingService.fetch(action.listingId).pipe(
            mergeMap((result) => {
              const listing: Listing = new Listing({ ...result });
              return [ListingSearchActions.listingDisplaySelected({ listing })];
            }),
            catchError((err) => of(ListingSearchActions.listingSearchError({ error: err }))),
          );
        }

        // Couldn't find the map point, so resetting everything to undefined.
        return [
          ListingSearchActions.listingDisplaySelected({ listing: undefined }),
        ];
      }),
    ),
  { functional: true },
);

export const retrieveUserPointList$ = createEffect(
  (actions$ = inject(Actions), userCustomLocationService = inject(UserCustomLocationService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.retrieveUserPointList),
      switchMap(() =>
        userCustomLocationService.fetchAll().pipe(
          map((locations: IUserCustomLocation[]) =>
            ListingSearchActions.retrieveUserPointListSuccess({
              points: locations.map(
                (location) =>
                  new MapPoint({
                    id: location.id || 0,
                    name: location.name,
                    coordinates: new LatLon({
                      lat: location.coordinates.lat,
                      lon: location.coordinates.lon,
                    }),
                    photos: [],
                  }),
              ),
            }),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const updateUserPoint$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userCustomLocationService = inject(UserCustomLocationService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.updateUserPoint),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected === true) {
          if (action.point.id) {
            const point: IUserCustomLocation = new UserCustomLocation({
              id: action.point.id,
              name: action.point.name,
              coordinates: new LatLon({
                lat: action.point.coordinates.lat,
                lon: action.point.coordinates.lon,
              }),
            });

            return userCustomLocationService.update(point).pipe(
              map((location) => {
                const point: MapPoint = new MapPoint({
                  id: location.id || 0,
                  name: location.name,
                  photos: [],
                  coordinates: new LatLon({
                    lat: location.coordinates.lat,
                    lon: location.coordinates.lon,
                  }),
                });

                return ListingSearchActions.updateUserPointSuccess({ point });
              }),
              catchError((error) => [ListingSearchActions.updateUserPointFailed({ error })]),
            );
          } else {
            return [ListingSearchActions.updateUserPointFailed({ error: 'Point identifier not supplied.' })];
          }
        } else {
          // @todo refactor this function for offline functionality.
          //  Not a big deal yet, since this can only be accessed from the map view, which isn't available offline currently.
          return [
            AlertActions.displayAlert({
              title: 'Function Not Available',
              body: 'This function is not currently supported in offline mode.',
              level: 'warning',
            }),
          ];
        }
      }),
    ),
  { functional: true },
);

export const updateUserPointSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingSearchActions.updateUserPointSuccess),
      mergeMap((action) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.DEBUG,
              message: 'Updated User Point',
              details: action,
              subjectId: action.point.id,
              subjectType: 'user-custom-location',
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const updateUserPointFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingSearchActions.updateUserPointFailed),
      mergeMap((action) => {
        return [
          AlertActions.displayAlert({
            title: 'Failed to Update User Point',
            body:
              'An error occurred while trying to update the user point: ' + (typeof action.error === 'object')
                ? JSON.stringify(action.error)
                : action.error,
            level: 'error',
          }),
          LogActions.logMessage({
            log: {
              level: LogLevel.ERROR,
              message: 'Failed to Update User Point',
              details: action,
              subjectType: 'user-custom-location',
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const removeUserPoint$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userCustomLocationService = inject(UserCustomLocationService)) =>
    actions$.pipe(
      ofType(ListingSearchActions.removeUserPoint),
      withLatestFrom(store.select(NetworkSelectors.getStatus)),
      mergeMap(([action, status]) => {
        if (status.connected === true) {
          if (action.point.id) {
            const point: IUserCustomLocation = new UserCustomLocation({
              id: action.point.id,
              name: action.point.name,
              coordinates: new LatLon({
                lat: action.point.coordinates.lat,
                lon: action.point.coordinates.lon,
              }),
            });
            return userCustomLocationService.remove(point).pipe(
              map((location) => {
                const point: MapPoint = new MapPoint({
                  id: location.id || 0,
                  name: location.name,
                  photos: [],
                  coordinates: new LatLon({
                    lat: location.coordinates.lat,
                    lon: location.coordinates.lon,
                  }),
                });
                return ListingSearchActions.removeUserPointSuccess({ point });
              }),
              catchError((error) => [ListingSearchActions.removeUserPointFailed({ error })]),
            );
          } else {
            return [ListingSearchActions.removeUserPointFailed({ error: 'Point identifier not supplied.' })];
          }
        } else {
          // @todo refactor this function for offline functionality.
          //  Not a big deal yet, since this can only be accessed from the map view, which isn't available offline currently.
          return [
            AlertActions.displayAlert({
              title: 'Function Not Available',
              body: 'This function is not currently supported in offline mode.',
              level: 'warning',
            }),
          ];
        }
      }),
    ),
  { functional: true },
);

export const removeUserPointSuccess$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingSearchActions.removeUserPointSuccess),
      mergeMap((action) => {
        return [
          LogActions.logMessage({
            log: {
              level: LogLevel.DEBUG,
              message: 'Removed User Point',
              details: action,
              subjectId: action.point.id,
              subjectType: 'user-custom-location',
            },
          }),
        ];
      }),
    ),
  { functional: true },
);

export const removeUserPointFailed$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ListingSearchActions.removeUserPointFailed),
      mergeMap((action) => [
        AlertActions.displayAlert({
          title: 'Failed to Remove User Point',
          body:
            'An error occurred while trying to remove the user point: ' + (typeof action.error === 'object')
              ? JSON.stringify(action.error)
              : action.error,
          level: 'error',
        }),
        LogActions.logMessage({
          log: {
            level: LogLevel.ERROR,
            message: 'Failed to Remove User Point',
            details: action,
            subjectType: 'user-custom-location',
          },
        }),
      ]),
    ),
  { functional: true },
);
